import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { useCustomerAuth } from './CustomerAuthContext';

const API = process.env.REACT_APP_API_URL || '';

interface WishlistContextType {
  wishlistIds: Set<number>;
  isWishlisted: (id: number) => boolean;
  toggleWishlist: (id: number) => Promise<void>;
  loading: boolean;
  refresh: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, token } = useCustomerAuth();
  const [wishlistIds, setWishlistIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !token) { setWishlistIds(new Set()); return; }
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/wishlist/ids`, { headers: authHeader });
      const json = await res.json();
      if (json.success) setWishlistIds(new Set(json.data as number[]));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, authHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const isWishlisted = useCallback((id: number) => wishlistIds.has(id), [wishlistIds]);

  const toggleWishlist = useCallback(async (id: number) => {
    if (!isAuthenticated || !token) return;
    const already = wishlistIds.has(id);
    // Optimistic update
    setWishlistIds(prev => {
      const next = new Set(prev);
      already ? next.delete(id) : next.add(id);
      return next;
    });
    try {
      await fetch(`${API}/api/wishlist/${id}`, {
        method: already ? 'DELETE' : 'POST',
        headers: authHeader,
      });
    } catch {
      // Revert on failure
      setWishlistIds(prev => {
        const next = new Set(prev);
        already ? next.add(id) : next.delete(id);
        return next;
      });
    }
  }, [isAuthenticated, token, wishlistIds, authHeader]);

  return (
    <WishlistContext.Provider value={{ wishlistIds, isWishlisted, toggleWishlist, loading, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be inside WishlistProvider');
  return ctx;
};
